import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useApolloClient } from "@apollo/client";
import { GET_STAFF_MANAGEMENT_CHECK_IN_HISTORY } from "graphql/query/staff-management-gql";


const useCheckInPositionDate = ({ eventID, enabled = true }) => {
    const client = useApolloClient();
    const normalizedEventID = String(eventID ?? "").trim();
    const activation = useMemo(() => ({
        client,
        eventID: normalizedEventID,
        enabled: Boolean(enabled && normalizedEventID),
    }), [ normalizedEventID, enabled, client ]);
    const activeLifetimeRef = useRef(null);
    const [ state, setState ] = useState({ activation: null, value: null, loading: false });

    useLayoutEffect(() => {
        const lifetime = { activation, request: null };
        activeLifetimeRef.current = lifetime;

        return () => {
            if (activeLifetimeRef.current === lifetime)
            {
                activeLifetimeRef.current = null;
            }
        };
    }, [ activation ]);

    const refresh = useCallback(({ force = false } = {}) => {
        const lifetime = activeLifetimeRef.current;

        if (!activation.enabled || lifetime?.activation !== activation)
        {
            return Promise.resolve(null);
        }

        if (!force && lifetime.request) return lifetime.request.promise;

        const request = { promise: null };
        const isCurrent = () => activeLifetimeRef.current === lifetime && lifetime.request === request;
        lifetime.request = request;
        setState({ activation, value: null, loading: true });

        request.promise = (async () => {
            let value = null;

            try
            {
                const result = await activation.client.query({
                    query: GET_STAFF_MANAGEMENT_CHECK_IN_HISTORY,
                    variables: {
                        where: { column: "EE_ID", operator: "EQ", value: activation.eventID },
                        orderBy: [
                            { column: "CREATED_AT", order: "DESC" },
                            { column: "ID", order: "DESC" },
                        ],
                        first: 1,
                        page: 1,
                    },
                    fetchPolicy: "network-only",
                    // A new activation or forced refresh must not join an older network request.
                    context: { queryDeduplication: false },
                });
                const log = result?.data?.exhibitionEventCheckinLogs?.data?.[ 0 ];
                const matchesEvent = log?.ee_id === undefined || log?.ee_id === null ||
                    String(log.ee_id) === activation.eventID;

                if (!result?.errors?.length && matchesEvent && typeof log?.created_at === "string")
                {
                    value = log.created_at.trim() || null;
                }
            } catch (error)
            {
                // A date lookup failure leaves exports available with an unavailable-date label.
            }

            if (!isCurrent()) return null;

            setState({ activation, value, loading: false });
            return { value };
        })().finally(() => {
            if (lifetime.request === request) lifetime.request = null;
        });

        return request.promise;
    }, [ activation ]);

    useEffect(() => {
        refresh();
    }, [ refresh ]);

    const currentState = state.activation === activation;

    return {
        value: activation.enabled && currentState ? state.value : null,
        loading: activation.enabled && (!currentState || state.loading),
        refresh,
    };
};

export default useCheckInPositionDate;
